import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import {
  login as apiLogin,
  logout as apiLogout,
  signup as apiSignup,
  sendMagicLink as apiSendMagicLink,
  getProfile,
  changeCurrentCompany as apiChangeCompany,
  hasValidToken,
  UserProfile,
  LoginCredentials,
  SignupData
} from '../services/auth'
import {
  startImpersonation as apiStartImpersonation,
  stopImpersonationApi,
  stashAdminSession,
  popAdminSession,
  setImpersonationMeta,
  clearImpersonationMeta,
  getImpersonationMeta,
  isImpersonating as readIsImpersonating
} from '../services/impersonation'

interface AuthContextType {
  user: UserProfile | null
  loading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  signup: (data: SignupData) => Promise<void>
  sendMagicLink: (email: string) => Promise<void>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
  changeCurrentCompany: (companyId: string, companyType: 'shipper' | 'freight-forwarder') => Promise<void>
  setAuthData: (user: UserProfile) => void
  isAuthenticated: boolean
  // Impersonation
  isImpersonating: boolean
  impersonationExpiresAt: number | null
  startImpersonation: (params: { targetUserId?: string; targetEmail?: string; reason?: string }) => Promise<void>
  stopImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isImpersonating, setIsImpersonating] = useState<boolean>(readIsImpersonating())
  const [impersonationExpiresAt, setImpersonationExpiresAt] = useState<number | null>(
    () => getImpersonationMeta()?.expiresAt || null
  )

  // Carrega o perfil do usuário ao montar
  useEffect(() => {
    loadUser()
  }, [])

  async function loadUser() {
    if (!hasValidToken()) {
      setLoading(false)
      return
    }

    try {
      const profile = await getProfile()
      setUser(profile)

      // If user has companies but no current_company, refresh profile to trigger auto-selection
      if (profile.companies.length > 0 && !profile.current_company) {
        console.log('User has companies but no current_company, refreshing profile...')
        setTimeout(async () => {
          try {
            const updatedProfile = await getProfile()
            setUser(updatedProfile)
          } catch (err) {
            console.error('Failed to refresh profile:', err)
          }
        }, 500)
      }
    } catch (error) {
      console.error('Failed to load user:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  async function login(credentials: LoginCredentials) {
    const data = await apiLogin(credentials)
    setUser(data.user)
  }

  async function signup(data: SignupData) {
    await apiSignup(data)
    // Após signup, usuário precisa confirmar email ou fazer login
  }

  async function sendMagicLink(email: string) {
    await apiSendMagicLink(email)
  }

  async function logout() {
    await apiLogout()
    setUser(null)
  }

  async function refreshProfile() {
    const profile = await getProfile()
    setUser(profile)
  }

  async function changeCurrentCompany(companyId: string, companyType: 'shipper' | 'freight-forwarder') {
    await apiChangeCompany(companyId, companyType)
    await refreshProfile()
  }

  function setAuthData(userData: UserProfile) {
    setUser(userData)
  }

  async function startImpersonation(params: { targetUserId?: string; targetEmail?: string; reason?: string }) {
    const currentAccess = localStorage.getItem('access_token')
    const currentRefresh = localStorage.getItem('refresh_token')

    if (!currentAccess || !currentRefresh || !user) {
      throw new Error('Sessão inválida para impersonar')
    }

    const result = await apiStartImpersonation(
      params.targetUserId || null,
      params.targetEmail || null,
      params.reason
    )

    // Stash admin session before swapping tokens
    stashAdminSession({ access_token: currentAccess, refresh_token: currentRefresh, user })

    // Replace tokens with impersonation token (no refresh token for impersonation)
    localStorage.setItem('access_token', result.access_token)
    localStorage.removeItem('refresh_token')

    setImpersonationMeta(result.audit_log_id, result.expires_at)
    setIsImpersonating(true)
    setImpersonationExpiresAt(result.expires_at)
    setUser(result.user)
  }

  async function stopImpersonation() {
    const meta = getImpersonationMeta()
    if (meta) {
      try {
        await stopImpersonationApi(meta.auditLogId)
      } catch (err) {
        console.error('Failed to mark impersonation as ended:', err)
        // Continue anyway — restore admin session locally
      }
    }

    const stashed = popAdminSession()
    clearImpersonationMeta()
    setIsImpersonating(false)
    setImpersonationExpiresAt(null)

    if (stashed) {
      localStorage.setItem('access_token', stashed.access_token)
      localStorage.setItem('refresh_token', stashed.refresh_token)
      setUser(stashed.user)
    } else {
      // No admin session to restore — force logout
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    signup,
    sendMagicLink,
    logout,
    refreshProfile,
    changeCurrentCompany,
    setAuthData,
    isAuthenticated: !!user,
    isImpersonating,
    impersonationExpiresAt,
    startImpersonation,
    stopImpersonation,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
